import { Alert, Checkbox, Flex, Input, InputNumber, Radio, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  TICKET_COMPLEXITY_REASON,
  TicketComplexityReasonLabels,
} from '@/model/Ticket/TicketConstants';

import type { TicketComplexitySurveyController } from './useTicketComplexitySurvey';

interface TicketComplexitySurveyFormProps {
  survey: TicketComplexitySurveyController;
}

// Metadata: formulario reusable para mostrar la encuesta dentro de su modal propio o encadenada.
export const TicketComplexitySurveyForm = (props: TicketComplexitySurveyFormProps) => {
  const { t } = useTranslation();
  const { survey } = props;
  const expectedTimeLabel =
    survey.expectedTimeMinutes != null
      ? formatDuration(survey.expectedTimeMinutes)
      : t('No disponible');

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Metadata: muestra el tiempo esperado que llega desde la subcategoría del ticket. */}
      <Alert
        type={survey.expectedTimeMinutes != null ? 'info' : 'warning'}
        showIcon
        message={`${t('Tiempo esperado de la subcategoría')}: ${expectedTimeLabel}`}
        style={{ marginBottom: 8 }}
      />

      {/* Pregunta principal */}
      <div>
        <p style={{ fontWeight: 500, marginBottom: 8 }}>
          {t('¿Se resolvió en tiempos y esfuerzo lógicos para la subcategoría del ticket?')}
        </p>
        <Radio.Group
          value={survey.resolvedInTime}
          onChange={e => survey.setResolvedInTime(e.target.value)}
        >
          <Radio value={true}>{t('Sí')}</Radio>
          <Radio value={false}>{t('No')}</Radio>
        </Radio.Group>
      </div>

      {/* Sección visible solo si la respuesta es "No" */}
      {survey.resolvedInTime === false && (
        <>
          {/* Metadata: registra duración real en formato HH:mm y se envía normalizada a minutos. */}
          <div>
            <p style={{ fontWeight: 500, marginBottom: 8 }}>{t('Tiempo real insumido')}</p>
            <Flex align="center" gap={8} wrap>
              <InputNumber
                min={0}
                precision={0}
                value={survey.actualTimeHours}
                onChange={survey.setActualTimeHours}
                addonAfter={t('h')}
                style={{ width: 120 }}
              />
              <Typography.Text>:</Typography.Text>
              <InputNumber
                min={0}
                max={59}
                precision={0}
                value={survey.actualTimeMinutes}
                onChange={survey.setActualTimeMinutes}
                addonAfter={t('min')}
                style={{ width: 130 }}
              />
            </Flex>
            <Typography.Text type="secondary">
              {t('Indique cuánto demoró realmente en horas y minutos')}
            </Typography.Text>
          </div>

          <div>
            <p style={{ fontWeight: 500, marginBottom: 8 }}>{t('¿Por qué fue más complejo?')}</p>
            <Space direction="vertical" size="small">
              {Object.values(TICKET_COMPLEXITY_REASON).map(reason => (
                <Checkbox
                  key={reason}
                  checked={survey.reasons.includes(reason)}
                  onChange={e => survey.handleReasonChange(reason, e.target.checked)}
                >
                  {t(TicketComplexityReasonLabels[reason])}
                </Checkbox>
              ))}
            </Space>
          </div>

          {/* Texto libre si se seleccionó "Otros" */}
          {survey.reasons.includes(TICKET_COMPLEXITY_REASON.OTHER) && (
            <div>
              <p style={{ fontWeight: 500, marginBottom: 8 }}>
                {t('Describe brevemente el motivo')}
              </p>
              <Input.TextArea
                rows={3}
                value={survey.otherText}
                onChange={e => survey.setOtherText(e.target.value)}
                placeholder={t('Escribe aquí...')}
              />
            </div>
          )}
        </>
      )}
    </Space>
  );
};

// Metadata: convierte minutos totales a una lectura compacta para comparar con el tiempo real.
const formatDuration = (minutes: number) => {
  const normalizedMinutes = Math.max(0, Math.trunc(minutes));
  const hours = Math.floor(normalizedMinutes / 60);
  const remainingMinutes = normalizedMinutes % 60;

  if (hours > 0 && remainingMinutes > 0) return `${hours} h ${remainingMinutes} min`;
  if (hours > 0) return `${hours} h`;

  return `${remainingMinutes} min`;
};
